import { EventEmitter, inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ICmsAdminUtilAbstractService } from '../service/icms-admin-util.abstract.service';
import { DfEventService } from '@df/core/service/df-event';
import { IAdminProviderEvent } from '@icms/core/integration/events';

@Injectable()
export abstract class ICmsAdminAbstractProvider<T, A = undefined> extends ICmsAdminUtilAbstractService {
    /**
     * Value that service provides
     */
    private values = new Map<A | undefined, T>();

    /**
     * Subject to subscribe to generation events
     */
    readonly generation = new Subject<IAdminProviderEvent<T, A>>();

    /**
     * Value change notification triggered on cache invalidation
     */
    readonly update$ = new EventEmitter<A>();

    /**
     * Methods that provides the value that will be retirned
     */
    protected abstract prepareValue(...args): T;

    /**
     * Events service
     */
    private dfEventService = inject(DfEventService);

    /**
     * Provider id used with events
     */
    protected abstract providerId: string;

    /**
     * Public value getter
     */
    getValue(cacheKeyParam?: A, ...args): T {
        let value = this.values.get(cacheKeyParam);
        if (!value) {
            const event: IAdminProviderEvent<T, A> = {
                value: this.prepareValue(cacheKeyParam, ...args),
                arg: cacheKeyParam
            };
            value = event.value;
            this.generation.next(event);
            this.dfEventService.dispatch({
                id: 'iCmsProvide',
                data: {
                    providerId: this.providerId,
                    ...event
                },
                action: data => {
                    this.values.set(cacheKeyParam, data.value);
                    return undefined;
                }
            });
        }
        return value;
    }

    invalidate(cacheKeyParam?: A) {
        if (cacheKeyParam) {
            this.values.delete(cacheKeyParam);
            this.update$.emit(cacheKeyParam);
        } else {
            this.values.clear();
            this.update$.emit();
        }
    }
}
